/**
 * El panel del técnico, sin React (3/11, B3): en qué bloque va cada cita, los filtros y el progreso.
 * Se prueba con Jest (panelTecnico.test.js).
 *
 * Bloques:
 *   sinCerrar   ABIERTAS cuya hora de fin ya pasó (hora de Madrid): se quedaron sin cerrar. Arriba y
 *               a la vista, la más antigua primero.
 *   hoy         lo de HOY (Madrid) que no está en sinCerrar ni cancelado: abiertas que aún no han
 *               terminado y las ya cerradas hoy.
 *   proximas    abiertas de días que vienen («Mi agenda», agrupadas por día con agruparAgenda).
 *   pasadas     cerradas (realizada, no asistió) de días anteriores, la más reciente primero
 *               (en pantalla, «Cerradas»).
 *   canceladas  aparte, al final y plegadas: no se mezclan con las pendientes.
 *
 * «Hoy» es la fecha de Madrid (horaMadrid.js), no la de UTC: antes, entre las 00:00 y la 01:00 (02:00
 * en verano) las citas de hoy salían en «Próximas».
 */
import { ahoraEnMadrid, citaYaTermino } from '../compartidos/horaMadrid';
import { esCitaAbierta, esCitaCancelada } from '../compartidos/etiquetasEstado';
import { ordenarPasadas } from './ordenIntervenciones';

const clave = (c) => `${c.fecha || ''} ${c.hora_inicio || ''}`;
const ascendente = (lista) => [...lista].sort((a, b) => clave(a).localeCompare(clave(b)));

export function agruparPanel(citas, ahora = new Date()) {
  const { hoy } = ahoraEnMadrid(ahora);
  const g = { sinCerrar: [], hoy: [], proximas: [], pasadas: [], canceladas: [] };
  for (const c of citas || []) {
    if (esCitaCancelada(c.estado)) g.canceladas.push(c);
    else if (esCitaAbierta(c.estado) && (c.fecha < hoy || citaYaTermino(c, ahora))) g.sinCerrar.push(c);
    else if (c.fecha === hoy) g.hoy.push(c);
    else if (c.fecha > hoy) g.proximas.push(c);
    else g.pasadas.push(c);
  }
  return {
    sinCerrar: ascendente(g.sinCerrar),
    hoy: ascendente(g.hoy),
    proximas: ascendente(g.proximas),
    pasadas: ordenarPasadas(g.pasadas),
    canceladas: ordenarPasadas(g.canceladas),
  };
}

/** 'YYYY-MM-DD' + n días, sin pasar por la zona del navegador (aritmética en UTC a mediodía). */
export function sumarDias(fecha, n) {
  const d = new Date(`${fecha}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const NOMBRE_DIA = new Intl.DateTimeFormat('es-ES', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });
const mayuscula = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** «Hoy», «Mañana» o «Jueves 5 de noviembre»: el día de una fecha 'YYYY-MM-DD' respecto a hoy en Madrid. */
export function tituloDia(fecha, ahora = new Date()) {
  const { hoy } = ahoraEnMadrid(ahora);
  if (fecha === hoy) return 'Hoy';
  if (fecha === sumarDias(hoy, 1)) return 'Mañana';
  return mayuscula(NOMBRE_DIA.format(new Date(`${fecha}T12:00:00Z`)).replace(',', ''));
}

/**
 * «Mi agenda» (3/11): las próximas citas agrupadas por día, en orden, cada día con sus citas por
 * hora. [{ fecha, titulo, citas }]. `proximas` es el bloque de agruparPanel (ya sólo días que vienen).
 */
export function agruparAgenda(proximas, ahora = new Date()) {
  const porDia = new Map();
  for (const c of ascendente(proximas || [])) {
    if (!porDia.has(c.fecha)) porDia.set(c.fecha, []);
    porDia.get(c.fecha).push(c);
  }
  return [...porDia.entries()].map(([fecha, citas]) => ({ fecha, titulo: tituloDia(fecha, ahora), citas }));
}

/** { hechas, total } de las subtareas de una cita del panel, o null si la campaña no tiene. */
export function progresoCita(cita) {
  const total = cita?.subtareas?.length || 0;
  if (!total) return null;
  return { hechas: cita.subtareas.filter((s) => s.completada).length, total };
}

/** Las campañas que aparecen en las citas, sin repetir, por título. */
export function campanasDe(citas) {
  const m = new Map();
  for (const c of citas || []) if (!m.has(c.campana_id)) m.set(c.campana_id, c.campana_titulo || `Campaña ${c.campana_id}`);
  return [...m.entries()].map(([id, titulo]) => ({ id, titulo })).sort((a, b) => a.titulo.localeCompare(b.titulo, 'es'));
}

const sinAcentos = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Filtros del panel. `estado`: 'todas' | 'abiertas' | 'cerradas' (realizada o no asistió) |
 * 'canceladas'. `persona`: texto, sin distinguir mayúsculas ni acentos. `campana`: id o '' (todas).
 */
export function filtrarCitas(citas, { campana = '', estado = 'todas', persona = '' } = {}) {
  const texto = sinAcentos(persona);
  return (citas || []).filter((c) => {
    if (campana !== '' && campana != null && String(c.campana_id) !== String(campana)) return false;
    if (estado === 'abiertas' && !esCitaAbierta(c.estado)) return false;
    if (estado === 'cerradas' && !['realizada', 'no_asistio'].includes(c.estado)) return false;
    if (estado === 'canceladas' && !esCitaCancelada(c.estado)) return false;
    if (texto && !sinAcentos(c.usuario?.nombre_completo).includes(texto)) return false;
    return true;
  });
}
